import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withStyles } from '@material-ui/core/styles'
import { AppPage, SearchField, SelectButton } from 'src/components/app-ui'
import CreateCourseButton from 'src/components/create-course'
import { courseFilterAction } from 'src/reducers/course/action'
import useDebouncedCallback from 'src/hooks/useDebouncedCallback'
import useTrackerFunction from 'src/hooks/useTrackerFunction'
import {
  DEFAULT_SORT,
  SORT_OPTIONS,
  toSortParams,
} from 'src/utils/library-sort'
import CourseLibraryCard from './components/course-library-card'
import styles from './styles'

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
]

const matchesStatus = (course, status) => {
  if (status === 'published') return course.isPublished !== false
  if (status === 'draft') return course.isPublished === false
  return true
}

const Courses = ({ classes }) => {
  const dispatch = useDispatch()
  const courses = useSelector(state => state.course.independentCourseList)
  // Card menus (duplicate, delete, thumbnail) update this local copy directly.
  const [localCourses, setLocalCourses] = useState([])
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [status, setStatus] = useState('all')
  const [loaded, setLoaded] = useState(false)
  const trackCreateCourse = useTrackerFunction('clicked_create_course', {
    type: 'independent',
  })

  const fetchCourses = useCallback(
    (filterText, sortOption) => {
      dispatch(
        courseFilterAction(
          {
            filterText,
            ...toSortParams(sortOption, 'name'),
            currentPage: 0,
            pageSize: 100,
            independent: true,
          },
          () => setLoaded(true),
        ),
      )
    },
    [dispatch],
  )
  const debouncedFetch = useDebouncedCallback(fetchCourses, 500)

  useEffect(() => {
    fetchCourses('', DEFAULT_SORT)
  }, [fetchCourses])

  useEffect(() => {
    setLocalCourses(courses || [])
  }, [courses])

  const handleSearch = value => {
    setSearch(value)
    if (value === '') fetchCourses(value, sort)
    else debouncedFetch(value, sort)
  }

  const handleSort = value => {
    setSort(value)
    fetchCourses(search, value)
  }

  const visibleCourses = useMemo(
    () => localCourses.filter(course => matchesStatus(course, status)),
    [localCourses, status],
  )

  return (
    <AppPage
      title="Courses"
      subtitle="Every course in this workspace."
      actionSlot={
        <CreateCourseButton
          variant="head"
          onTrack={() => trackCreateCourse()}
        />
      }
    >
      <div className={classes.list}>
        <div className={classes.toolbar}>
          <SearchField
            value={search}
            onChange={handleSearch}
            placeholder="Search courses..."
          />
          <div className={classes.filters}>
            <SelectButton
              options={STATUS_OPTIONS}
              value={status}
              onChange={setStatus}
            />
            <SelectButton
              options={SORT_OPTIONS}
              value={sort}
              onChange={handleSort}
            />
          </div>
        </div>

        {visibleCourses.length > 0 ? (
          <div className={classes.grid}>
            {visibleCourses.map(course => (
              <CourseLibraryCard
                key={course.id}
                course={course}
                localCourses={localCourses}
                setLocalCourses={setLocalCourses}
              />
            ))}
          </div>
        ) : (
          loaded && (
            <div className={classes.empty}>
              {search || status !== 'all'
                ? 'No courses match your filters.'
                : 'No courses yet. Create your first course to get started.'}
            </div>
          )
        )}
      </div>
    </AppPage>
  )
}

export default withStyles(styles)(Courses)
